import { FC, MouseEvent, RefObject, useRef, useState } from 'react';
import { Box, CircularProgress, Grid, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import Form, { FormDataModel } from '../../../components/Form';
import { LoginForm } from './LoginForm';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import { removeErrorFieldsFromValues } from '../../../utils/validators';
import { URLS } from '../../../utils/constants/urls';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserContext, initData, setAuthTokens, setUser } from 'utils/redux/reducer/authentication-slice';
import axiosInstance from '../../../utils/redux/axios';
import strings from '../../../common/Translation/Translate';
import { setNotificationMessage } from '../../../utils/redux/reducer/Notification-details-slice';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import RecaptchaNotice from '../../../components/Captcha/RecaptchaNotice';
import { ChangeEmailFormDialog } from 'components/Dialog/ChangeEmailFormDialog';
import { RECAPTCHA_ACTIONS } from '../../../utils/recaptchaActions';

const Login: FC<any> = ({ handleClose, setDrawerState }: { handleClose: any; setDrawerState: any }) => {
  const theme = useTheme();
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  let loginForm: RefObject<Form | null | undefined> = useRef();
  const [hasError, setHasError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [changeEmailFormDialogOpen, setChangeEmailFormDialogOpen] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const inititalData = useSelector(initData);

  const resendOtpFunc = async () => {
    try {
      setLoading(true);
      const body = {
        email: email,
        mailType: 'REGISTRATION',
      };
      await axiosInstance.post(URLS.resendVerificationCode, body);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
    }
  };
  const handleLogin = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = loginForm.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const bodyData = removeErrorFieldsFromValues(formData);
    try {
      setHasError(false);
      if (isFormValid) {
        if (!executeRecaptcha) {
          dispatch(
            setNotificationMessage({
              display: true,
              severity: 'warning',
              message: 'reCAPTCHA is not ready',
            }),
          );
          return false;
        }
        setEmail(bodyData.email as string);
        setLoading(true);
        const captchaRes = await executeRecaptcha(RECAPTCHA_ACTIONS.login);
        const response = await axiosInstance.post(URLS.keycloakLogin, {
          username: bodyData.email,
          password: bodyData.password,
          captchaResponse: {
            captchaRes,
            captchaAction: RECAPTCHA_ACTIONS.login,
          },
          loginType: 'EMAIL_PASSWORD',
        });
        const { data }: { data: any } = response;
        setLoading(false);
        dispatch(
          setAuthTokens({
            accessToken: data?.accessToken ?? data?.access_token,
            refreshToken: data?.refreshToken ?? data?.refresh_token,
            organization: data?.organization,
            tenantId: data?.tenantId,
          }),
        );
        const loginEmail = String(bodyData.email ?? '');
        dispatch(setUser({ username: loginEmail, email: loginEmail }));
        try {
          await dispatch(fetchUserContext() as any);
        } catch {
          // Logged in with tokens; context can be retried from the app shell.
        }
        handleClose();
      } else {
        setHasError(true);
      }
    } catch (error: any) {
      setLoading(false);
      if (
        error.response.data.error_description === 'Please verify your email address to continue.' ||
        error.response.data.status === 403
      ) {
        setChangeEmailFormDialogOpen(true);
      } else {
        const errorMessage = error.response ? error.response.data.error_description : error.message;

        dispatch(
          setNotificationMessage({
            display: true,
            severity: 'error',
            message: errorMessage ? errorMessage : error.response.data.message,
          }),
        );
      }
    }
  };

  return (
    <>
      <Box display={'flex'} justifyContent={'flex-end'} px={5} py={2}>
        {!inititalData?.forcedLogin && (
          <ClearOutlinedIcon
            onClick={handleClose}
            sx={{
              ':hover': { cursor: 'pointer' },
              color: theme.palette.primary.main,
            }}
          />
        )}
      </Box>
      <Typography className="font-cinzel" fontWeight={'bold'} ml={8} mb={1} color={'primary'} fontSize={'20px'}>
        {strings.loginText}
      </Typography>
      <Box mx={screenSizeDownMd ? 3 : 8} bgcolor={theme.palette.secondary.light}>
        <Grid container mt={1} px={screenSizeDownMd ? 2 : 4} py={3} bgcolor={theme.palette.secondary.light} spacing={1}>
          <Form
            hasError={hasError}
            ref={loginForm as RefObject<Form>}
            model={LoginForm(strings)}
            values={{}}
            forgetPassword={() => setDrawerState(2)}
          />
          <Grid item xs={12}>
            <PrimaryButton disabled={loading} sx={{ mt: 1, py: 1 }} onClick={e => handleLogin(e)}>
              {loading && <CircularProgress sx={{ mr: 1 }} size={20} />}
              {strings.loginText}
            </PrimaryButton>
            <RecaptchaNotice />
          </Grid>
          <Grid item xs={12}>
            <Box display={'flex'} justifyContent="center">
              <Typography fontSize={'0.75rem'}>{strings.dontHaveAccountText}&nbsp;</Typography>
              <Link
                sx={{ cursor: 'pointer' }}
                color={theme.palette.primary.main}
                onClick={() => setDrawerState(3)}
                underline={'hover'}>
                <Typography fontWeight={500} fontSize={'0.75rem'}>
                  {strings.signUp}
                </Typography>
              </Link>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <ChangeEmailFormDialog
        setDrawerState={setDrawerState}
        open={changeEmailFormDialogOpen}
        handleClose={setChangeEmailFormDialogOpen}
        email={email}
        resendOtpFunc={resendOtpFunc}
        verifyOtpMailType={'REGISTRATION'}
        caseType="login"
        successMessage={strings.registrationSuccessText}
        header={strings.registrationTextLower}
      />
    </>
  );
};

export default Login;
